import { FC } from 'react';

import { Box } from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers-pro';

import { Dayjs } from 'dayjs';

type Props = PickersDayProps<Dayjs> & {
  isDateUnavailable?: (date: Dayjs) => boolean;
};

const CalendarDatePickerDay: FC<Props> = ({ isDateUnavailable, ...dayProps }) => {
  const { day, outsideCurrentMonth, selected, today } = dayProps;
  const unavailable = !outsideCurrentMonth && !!isDateUnavailable?.(day);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <PickersDay
        {...dayProps}
        disableMargin
        disableHighlightToday
        sx={{
          width: '44px',
          height: '40px',
          borderRadius: '12px',
          fontSize: '12px',
          lineHeight: 1.25,
          border: 'none',
          // MUI's default PickersDay applies a blue `primary.main`-based tint
          // on native DOM :focus, and the calendar's roving tabindex focuses
          // TODAY'S cell by default even with no value selected — so today
          // renders as if it were selected the moment the calendar opens.
          // Always clear it, not just for the plain unselected case.
          '&:focus': { backgroundColor: 'transparent !important' },
          '&.Mui-selected': {
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: 1,
            // The theme's global MuiPickersDay override sets its own
            // `&.Mui-selected` color at equal CSS specificity, so insertion
            // order (not intent) decides which one wins depending on which
            // MUI X component hosts this day slot. Force it so the pill
            // renders consistently everywhere.
            color: '#FEFEFE !important',
          },
          ...(!selected &&
            !unavailable && {
              '&:hover': { backgroundColor: (theme) => theme.palette.grey[50] },
            }),
          ...(unavailable && {
            backgroundColor: (theme) => theme.palette.red[200],
            color: (theme) => `${theme.palette.red[400]} !important`,
            fontWeight: 400,
            lineHeight: 1,
            textDecoration: 'line-through',
            '&:hover, &:focus': {
              backgroundColor: (theme) => theme.palette.red[200],
            },
          }),
        }}
      />
      {today && !selected && !outsideCurrentMonth && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '7px',
            left: '20px',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            backgroundColor: (theme) => theme.palette.accent.main,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default CalendarDatePickerDay;
